import { useCallback, useEffect, useRef, useState } from 'react'
import { useClearBodyNoScrollClass } from '../../hooks/use-clear-body-no-scroll-slass'
import {
  defaultDocumentFilename,
  fetchPdfBlob,
} from '../../utils/fetchPdfBlob'
import {
  installPwaPdfGuards,
  OPEN_PDF_EVENT,
  type OpenPdfDetail,
} from '../../utils/openPdf'
import { PdfViewerActionButtons } from '../report-pdf-menu/ReportPdfMenu'
import PdfPages from './PdfPages'
import styles from './PdfViewer.module.css'

const MIN_ZOOM = 0.75
const MAX_ZOOM = 3
const ZOOM_STEP = 0.25

function clampZoom(value: number) {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, value))
}

function touchDistance(touches: TouchList) {
  const a = touches.item(0)
  const b = touches.item(1)
  if (!a || !b) return 0
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY)
}

function PdfViewerShell({
  doc,
  onClose,
}: {
  doc: OpenPdfDetail
  onClose: () => void
}) {
  useClearBodyNoScrollClass()

  // Не зумим всю PWA-оболочку (иначе уезжают кнопки) — зум только у PDF
  useEffect(() => {
    const meta = document.querySelector('meta[name="viewport"]')
    if (!meta) return
    const prev = meta.getAttribute('content') || ''
    meta.setAttribute(
      'content',
      'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover'
    )
    return () => meta.setAttribute('content', prev)
  }, [])

  const bodyRef = useRef<HTMLDivElement>(null)
  const zoomRef = useRef(1)
  const pinchStartDistRef = useRef(0)
  const pinchStartZoomRef = useRef(1)

  const [blob, setBlob] = useState<Blob | null>(null)
  const [filename, setFilename] = useState(defaultDocumentFilename)
  const [error, setError] = useState(false)
  const [zoom, setZoom] = useState(1)

  const updateZoom = useCallback((next: number) => {
    const value = clampZoom(next)
    zoomRef.current = value
    setZoom(value)
  }, [])

  useEffect(() => {
    let cancelled = false
    setBlob(null)
    setFilename(defaultDocumentFilename())
    setError(false)
    updateZoom(1)

    fetchPdfBlob(doc.url, doc.title)
      .then((file) => {
        if (!cancelled) {
          setBlob(file.blob)
          setFilename(file.filename)
        }
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })

    return () => {
      cancelled = true
    }
  }, [doc.url, doc.title, updateZoom])

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [onClose])

  // Pinch-to-zoom по области документа
  useEffect(() => {
    const el = bodyRef.current
    if (!el) return

    const onTouchStart = (event: TouchEvent) => {
      if (event.touches.length === 2) {
        pinchStartDistRef.current = touchDistance(event.touches)
        pinchStartZoomRef.current = zoomRef.current
      }
    }

    const onTouchMove = (event: TouchEvent) => {
      if (event.touches.length !== 2 || pinchStartDistRef.current <= 0) return
      event.preventDefault()
      const scale = touchDistance(event.touches) / pinchStartDistRef.current
      updateZoom(pinchStartZoomRef.current * scale)
    }

    const onTouchEnd = (event: TouchEvent) => {
      if (event.touches.length < 2) {
        pinchStartDistRef.current = 0
      }
    }

    const onWheel = (event: WheelEvent) => {
      if (!(event.ctrlKey || event.metaKey)) return
      event.preventDefault()
      const factor = event.deltaY < 0 ? 1.08 : 1 / 1.08
      updateZoom(zoomRef.current * factor)
    }

    el.addEventListener('touchstart', onTouchStart, { passive: true })
    el.addEventListener('touchmove', onTouchMove, { passive: false })
    el.addEventListener('touchend', onTouchEnd)
    el.addEventListener('touchcancel', onTouchEnd)
    el.addEventListener('wheel', onWheel, { passive: false })

    return () => {
      el.removeEventListener('touchstart', onTouchStart)
      el.removeEventListener('touchmove', onTouchMove)
      el.removeEventListener('touchend', onTouchEnd)
      el.removeEventListener('touchcancel', onTouchEnd)
      el.removeEventListener('wheel', onWheel)
    }
  }, [updateZoom])

  return (
    <div className={styles.overlay} role="dialog" aria-modal="true">
      <div className={styles.header}>
        <button type="button" className={styles.close} onClick={onClose}>
          Закрыть
        </button>
        <div className={styles.title}>{doc.title || 'PDF'}</div>
        <div className={styles.zoomGroup}>
          <button
            type="button"
            className={styles.zoom}
            disabled={zoom <= MIN_ZOOM}
            onClick={() => updateZoom(zoom - ZOOM_STEP)}
          >
            −
          </button>
          <button
            type="button"
            className={styles.zoom}
            disabled={zoom >= MAX_ZOOM}
            onClick={() => updateZoom(zoom + ZOOM_STEP)}
          >
            +
          </button>
        </div>
        <PdfViewerActionButtons
          blob={blob}
          filename={filename || defaultDocumentFilename()}
          title={doc.title || 'PDF'}
          disabled={!blob}
        />
      </div>
      <div ref={bodyRef} className={styles.body}>
        {error && (
          <div className={styles.message}>Не удалось открыть документ</div>
        )}
        {!error && !blob && (
          <div className={styles.message}>Загрузка…</div>
        )}
        {blob && <PdfPages blob={blob} zoom={zoom} />}
      </div>
    </div>
  )
}

function PdfViewer() {
  const [doc, setDoc] = useState<OpenPdfDetail | null>(null)

  const close = useCallback(() => {
    setDoc((current) => {
      if (current?.url.startsWith('blob:')) {
        URL.revokeObjectURL(current.url)
      }
      return null
    })
    if (window.history.state?.pdfViewer) {
      window.history.back()
    }
  }, [])

  useEffect(() => {
    installPwaPdfGuards()

    const onOpen = (event: Event) => {
      const detail = (event as CustomEvent<OpenPdfDetail>).detail
      if (!detail?.url) return
      setDoc(detail)
      if (!window.history.state?.pdfViewer) {
        window.history.pushState({ pdfViewer: true }, '')
      }
    }

    const onPopState = () => {
      setDoc((current) => {
        if (current?.url.startsWith('blob:')) {
          URL.revokeObjectURL(current.url)
        }
        return null
      })
    }

    window.addEventListener(OPEN_PDF_EVENT, onOpen)
    window.addEventListener('popstate', onPopState)
    return () => {
      window.removeEventListener(OPEN_PDF_EVENT, onOpen)
      window.removeEventListener('popstate', onPopState)
    }
  }, [])

  if (!doc) return null
  return <PdfViewerShell doc={doc} onClose={close} />
}

export default PdfViewer
